import { AfterViewInit, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject, takeUntil } from "rxjs";
import { GeozoneViewModel } from "../Model/GeozoneViewModel";
import { ZoneService } from "../../../../services/zones.service";
import { GroupBaseAdminPanelComponent } from "../../base-admin-panel/group-base-admin-panel.component";
import { IManagableObject } from "../../IManagableObject";
import { map } from "rxjs/operators";
import { MapObject, SimpleMapComponent, MapPoint } from "../../../shared/components/simple-map/c.component";
import { IconViewModel } from "../../../../models/IconViewModel";
import { CreateGeozoneDialogComponent } from '../create-geozone-dialog/create-geozone-dialog.component';
import { ContainerAcl } from 'app/models/ContainerAcl';
import { ContainersService } from 'app/services/containers.service';
import { ContainerViewModel } from '../../ContainerViewModel';
import { GroupManagementDialogComponent } from '../../group-management-dialog/group-management-dialog.component';
import { ObjectsManagementDialogComponent } from '../../objects-management-dialog/objects-management-dialog.component';
import { ObjectGroupViewModel } from '../../ObjectGroupViewModel';
import { matchesSearchRule } from '../../../shared/pipes/filterByProperty';

declare const $;

@Component({
  selector: '[app-geozone]',
  templateUrl: './geozone.component.html',
  styleUrls: ['./geozone.component.css'],
  standalone: false
})
export class GeozoneComponent extends GroupBaseAdminPanelComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(SimpleMapComponent, { static: true }) map: SimpleMapComponent;

  private zonesService: ZoneService;
  private containersService: ContainersService;
  public Icons: IconViewModel[] = [];
  public isEdit: boolean = false;
  public selectedGeozone: GeozoneViewModel;
  public canAddGeozoneGroup: boolean = false;
  public containerFilterId: number = 0;
  public containers: ContainerViewModel[] = [];
  private containerAclById = new Map<number, number>();
  private containerNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();


  constructor(protected injector: Injector) {
    super(injector);
    this.zonesService = injector.get(ZoneService);
    this.containersService = injector.get(ContainersService);
    this.groupName = "GeozonesGroup";
    this.entityName = "Geozone";
  }

  ngOnInit() {
    super.ngOnInit();

    this.usersService.onInit.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildContainerAclMap();
    });

    this.zonesService.GetZoneIcons()
      .subscribe(icons => {
        this.Icons = icons;

        let blank = new IconViewModel();
        blank.Url = "/assets/icons/blank.svg";
        this.Icons.splice(0, 0, blank);
      });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.onResize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onDrawFinish(points) {
    if (!this.selectedGeozone)
      return;

    this.selectedGeozone.Points = points.map(p => p.lat.toFixed(5) + " " + p.lng.toFixed(5)).join(" ");
  }

  protected Refresh() {
    this.loadContainers();

    this.zonesService.GetZones()
      .subscribe(zones => {
        this.objects = zones;
        //if (this.objects.length > 1000)
        //this.objects.length = 1000;
      });
    this.zonesService.GetGroups().subscribe(groups => {
      this.groupFilter.groups = groups;
    });
  }

  public onZoneChanged() {
    if (!this.selectedGeozone)
      return;

    var points = new Array<MapPoint>();
    var arr = this.selectedGeozone.Points.split(" ");

    for (var i = 0; i < Math.floor(arr.length / 2); i++)
      points.push(new MapPoint(parseFloat(arr[2 * i]), parseFloat(arr[2 * i + 1])));

    var obj = new MapObject();
    obj.label = this.selectedGeozone.Name;
    obj.points = points;
    obj.style = this.selectedGeozone.Style.Clone();

    obj.icon = "";
    for (let i = 0; i < this.Icons.length; i++)
      if (this.selectedGeozone.IconId == this.Icons[i].Id)
        obj.icon = this.Icons[i].Url;
    obj.modify = true;
    this.map.ShowObjects([obj], false);
  }

  public onGeozoneSelected(geozone: GeozoneViewModel) {
    if (!geozone)
      return;

    this.selectedGeozone = geozone;

    var obj = new MapObject();
    obj.geozoneId = geozone.Id;
    this.map.ShowObjects([obj], true);
  }

  public get filteredGeozones(): GeozoneViewModel[] {
    let items = this.objects as GeozoneViewModel[];

    const term = this.filter.term ?? "";
    if (term) {
      items = items.filter(zone => matchesSearchRule(zone.Name, term));
    }

    if (this.filter.group?.Id > 0) {
      const groupIds = new Set(this.filter.group.objects ?? []);
      items = items.filter(zone => groupIds.has(zone.Id));
    }

    if (this.containerFilterId > 0) {
      items = items.filter(zone => zone.ContainerId === this.containerFilterId);
    }

    return items;
  }

  public getContainerName(containerId?: number): string {
    if (!containerId)
      return "";

    return this.containerNameById.get(containerId) ?? "";
  }

  protected doDeleteObject(id: number): Observable<any> {
    return this.zonesService.DeleteZone(id);
  }

  public createZone() {
    if (!this.canAddGeozone()) return;
    this.editZone(new GeozoneViewModel());
  }

  public editZone(geozone: GeozoneViewModel) {
    this.isEdit = geozone ? true : false;
    this.selectedGeozone = geozone;

    if (this.isEdit)
      this.map.HideObjectsLayer();
    else
      this.map.ShowObjectsLayer();

    if (geozone) {
      this.onGeozoneSelected(geozone);
      this.onZoneChanged();
    }
    else
      this.map.ShowObjects([], false);
  }

  public onEditGeozone(geozone: GeozoneViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canEditGeozone(geozone)) return;
    this.editZone(geozone);
  }

  public onDeleteGeozone(geozone: GeozoneViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canDeleteGeozone(geozone)) return;
    this.deleteObject(geozone.Id);
  }

  protected doCreateGroup(input: IManagableObject): Observable<any> {
    return this.zonesService.CreateGroup(input);
  }

  protected doDeleteGroup(input: IManagableObject): Observable<any> {
    return this.zonesService.DeleteGroup(input);
  }

  public onSplitter(): void {
    var thisObj = this;
    $(document).bind("mousemove", function (e: any) {
      document.getSelection().removeAllRanges();
      var docW = $('.geozona-wrap').width();
      var pos = e.clientX - $('.geozona-wrap').offset().left;
      if (pos > 350 && pos < (docW - 350)) {
        $('.geozona-table').css({ 'flex-basis': pos + 'px', 'max-width': pos + 'px' });
        $('.geozona-map').css({ 'flex-basis': (docW - pos - 10) + 'px', 'max-width': (docW - pos - 10) + 'px' });
        thisObj.onResize();
      }
    });

    $(document).on("mouseup", function (e) {
      $(document).off("mousemove");
      $(document).off("mouseup");
    });
  }

  public onResize(): void {
    //$(window).trigger('resize');
  }

  Incomingfile(file) {
    if (!file) return;
    this.zonesService.Import(file)
      .subscribe((res) => {
        this.toastr.success(this.translate.instant("Import successful"));
        this.Refresh();
        this.syncService.NotifyEntityChanged(this.entityName, "");
      }, (err) => {
        this.toastr.error(this.translate.instant("Import failed"));
      });
  }

  OpenExport() {
    this.zonesService.export(this.objects.filter(c => c.IsChecked).map(o => o.Id))
      .pipe(map(res => {
        return {
          filename: 'export.kml',
          data: res
        };
      }))
      .subscribe((res: any) => {
        var url = window.URL.createObjectURL(res.data);
        var a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        a.download = res.filename;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove(); // remove the element
        // },
      },
        (err) => {
          this.toastr.error(this.translate.instant("Export failed"));
        });
  }

  public createDuplicate(zone: GeozoneViewModel) {
    this.zonesService.GetZone(zone.Id)
      .subscribe(zone => {
        let clone: GeozoneViewModel = Object.assign(new GeozoneViewModel(), zone);

        clone.Id = 0;
        clone.Objects = [];
        clone.GroupIds = [];
        //на всякий случай
        clone.Groups = [];

        this.editZone(clone);
      });
  }

  public canAddGeozone(): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    for (const acl of this.containerAclById.values()) {
      if ((acl & ContainerAcl.CreateGeozone) === ContainerAcl.CreateGeozone)
        return true;
    }

    return false;
  }

  public canEditGeozone(geozone: GeozoneViewModel): boolean {
    return this.hasGeozonePermission(geozone, ContainerAcl.EditGeozone);
  }

  public canDeleteGeozone(geozone: GeozoneViewModel): boolean {
    return this.hasGeozonePermission(geozone, ContainerAcl.DeleteGeozone);
  }

  private hasGeozonePermission(geozone: GeozoneViewModel, permission: ContainerAcl): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    if (!geozone?.ContainerId)
      return false;

    const acl = this.containerAclById.get(geozone.ContainerId) ?? 0;
    return (acl & permission) === permission;
  }

  private loadContainers() {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser) {
      this.containers = [];
      this.containerNameById = new Map<number, string>();
      this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddGeozoneGroup = false;
      return;
    }

    this.containersService.GetContainers().subscribe(containers => {
      this.containers = [...(containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.containerNameById = new Map(this.containers.map(container => [container.Id, container.Name] as [number, string]));
      if (this.containerFilterId > 0 && !this.containers.some(container => container.Id === this.containerFilterId))
        this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddGeozoneGroup = this.getCreatableContainers(this.containers).length > 0;
    });
  }

  public override addGroup() {
    const creatableContainers = this.getCreatableContainers(this.containers ?? []);
    if (!creatableContainers.length) return;

    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = creatableContainers;
    this.groupMgmtDialog.instance.readonlyContainer = false;
    this.initGeozoneGroupDialog();

    const group = new ObjectGroupViewModel();
    group.ContainerId = creatableContainers[0].Id;
    this.groupMgmtDialog.instance.open(group);
  }

  public override editGroup(group: ObjectGroupViewModel) {
    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers ?? [];
    this.groupMgmtDialog.instance.readonlyContainer = true;
    this.initGeozoneGroupDialog();
    this.groupMgmtDialog.instance.open(group);
  }

  private buildContainerAclMap() {
    const currentUser = this.usersService.getCurrentUser();
    this.containerAclById = new Map<number, number>();

    for (const container of this.containers) {
      this.containerAclById.set(container.Id, this.getContainerAcl(container));
    }
  }

  private getCreatableContainers(containers: ContainerViewModel[]): ContainerViewModel[] {
    return containers.filter(container =>
      (this.getContainerAcl(container) & ContainerAcl.CreateGeozone) === ContainerAcl.CreateGeozone);
  }

  private getContainerAcl(container: ContainerViewModel): number {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser)
      return 0;

    if (currentUser.CanDo(this.AccessRights.EDITUSERS) || currentUser.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return ContainerAcl.CreateGeozone | ContainerAcl.EditGeozone | ContainerAcl.DeleteGeozone;

    const userId = currentUser?.Id ?? 0;
    const aclFromUsers = container.Users?.find(link => link.UserId === userId)?.ACL;
    const fallbackAcl = (currentUser.ContainerLinks ?? []).find(link => link.ContainerId === container.Id)?.ACL;
    return aclFromUsers ?? fallbackAcl ?? 0;
  }

  private initGeozoneGroupDialog() {
    const allObjects = this.objects.map(x => Object.assign({}, x)) as GeozoneViewModel[];

    this.groupMgmtDialog.instance.onSubmit.subscribe(() => {
      const input = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      input.objects = this.groupMgmtDialog.instance.objects.map(zone => zone.Id);
      this.doCreateGroup(input).subscribe(() => {
        if (this.groupName)
          this.syncService.NotifyEntityChanged(this.groupName, input.Name);
        this.Refresh();
      });
      this.groupMgmtDialog.instance.close();
    });

    this.groupMgmtDialog.instance.onOpen.subscribe(() => {
      const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      this.groupMgmtDialog.instance.objects = this.getVisibleGroupZones(allObjects, group.ContainerId, group.objects)
        .filter(zone => (group.objects ?? []).indexOf(zone.Id) >= 0)
        .map(zone => {
          zone.IsChecked = false;
          return zone;
        });
    });

    this.groupMgmtDialog.instance.onAddObjects.subscribe(() => {
      this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
      this.objectsListDialog.instance.onOpen.subscribe(() => {
        const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
        const filteredObjects = this.getVisibleGroupZones(allObjects, group.ContainerId, group.objects)
          .filter(zone => this.groupMgmtDialog.instance.objects.find(existing => existing.Id == zone.Id) == undefined)
          .map(x => Object.assign({}, x))
          .map(zone => {
            zone.IsChecked = false;
            return zone;
          });

        this.objectsListDialog.instance.groups = this.groupFilter.groups;
        this.objectsListDialog.instance.objects = filteredObjects;
      });

      this.objectsListDialog.instance.onSubmit.subscribe(() => {
        this.objectsListDialog.instance.objects.forEach(zone => {
          if (zone && zone.IsChecked) {
            if (this.groupMgmtDialog.instance.objects.indexOf(zone) < 0) {
              zone.IsChecked = false;
              this.groupMgmtDialog.instance.objects.push(zone);
            }
          }
        });
        this.objectsListDialog.instance.close();
      });

      this.objectsListDialog.instance.open();
    });
  }

  private getVisibleGroupZones(allObjects: GeozoneViewModel[], containerId?: number, selectedIds?: number[]): GeozoneViewModel[] {
    const ids = selectedIds ?? [];
    return allObjects.filter(zone => {
      if (ids.includes(zone.Id))
        return true;

      if (!containerId)
        return false;

      return zone.ContainerId === containerId;
    });
  }
}
